import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'

/* Chat response format:
  {
    "id": "dfe2769b-f3a0-4916-a25b-ec212fcbf2b1",
    "thread_id": "chat_0e56d951-421a-4549-8b6c-860bc752a19d",
    "title": "Filtering Variants",
    "created_at": "2025-06-18T14:39:54.216306Z",
    "last_updated": "2025-06-18T14:39:54.224624Z",
    "response": "I have planned these filters. Please accept them to continue.",
    "interrupted": false,
    "filter_called": true,
    "tools": [
      {
        "name": "ACMG_filter",
        "args": { "ACMG": ["Pathogenic", "Likely pathogenic"] },
        "id": "call_0mATpgMcb2HcsfCuCn89Iybd",
        "type": "tool_call"
      }
    ],
    "filter_state": null
  }
*/

/**
 * Service for handling AI chat API calls
 */
const chatService = {
  /**
   * Fetch all chat threads
   * @returns {Promise} Promise with chat threads data
   */
  getChatThreads: async () => {
    const response = await axios.get(`${API_BASE_URL}/chats/`)
    /* Response data:
     * [
     *   {
     *     "id": "550e8400-e29b-41d4-a716-446655440000",
     *     "thread_id": "chat_550e8400-e29b-41d4-a716-446655440001",
     *     "title": "BRCA1 Variant Analysis",
     *     "created_at": "2024-01-15T10:30:00Z",
     *     "last_updated": "2024-01-15T11:45:30Z"
     *   }
     * ]
     */
    return response.data
  },

  /**
   * Fetch a specific chat thread with its history
   * @param {string} chatId - The ID of the chat to fetch
   * @returns {Promise} Promise with chat data
   */
  getChatHistory: async (chatId) => {
    const response = await axios.get(`${API_BASE_URL}/chats/${chatId}/`)
    /* Response data:
    {
      "external_messages": [
        {
          "role": "human",
          "content": "Find rare variants in the BRCA1 gene"
        },
        {
          "role": "ai",
          "content": "I have planned a filter for this. Please accept to continue."
        }
      ],
      "internal_messages": [ ... ]
    }
    */
    return response.data
  },

  getLastAgentAction: async (chatId) => {
    // Endpoint: GET /api/v1/chats/{chat_id}/?action=true
    const response = await axios.get(`${API_BASE_URL}/chats/${chatId}/?action=true`)
    /* Response data:
    {
      "action": "string",
    }
    */
    return response.data
  },

  /**
   * Create a new chat thread
   * @param {string} message - The message to send
   * @param {Object|null} current_filters - Optional current filters
   * @param {string|null} sample_id - Optional sample ID
   * @returns {Promise} Promise with new chat data
   */
  createChat: async (message, current_filters = null, sample_id = null) => {
    /* Request body:
      {
        "message": "Find rare variants in the BRCA1 gene.",
        "sample_id": "sample_123456",
        "current_filters": { ... }
      }
    */

    // Clean approach: build object and filter out null/undefined values
    const requestBody = Object.fromEntries(
      Object.entries({
        message,
        sample_id,
        current_filters,
      }).filter(([_, value]) => value != null),
    )

    const response = await axios.post(`${API_BASE_URL}/chats/`, requestBody)
    // We will get ChatResponse object in response.data
    return response.data
  },

  /**
   * Send a message to a chat thread
   * @param {string} chatId - The ID of the chat
   * @param {string} message - The message to send
   * @param {Object|null} currentFilters - Optional current filters
   * @param {string|null} sampleId - Optional sample ID
   * @param {boolean|null} accept - Optional accept
   * @param {Object|null} toolCalls - Optional tool calls
   * @returns {Promise} Promise with response data
   */
  sendMessage: async (
    chatId,
    message = null,
    currentFilters = null,
    sampleId = null,
    accept = null,
    toolCalls = null,
  ) => {
    const requestBody = Object.fromEntries(
      Object.entries({
        message,
        sample_id: sampleId,
        current_filters: currentFilters,
        accept,
        tool_calls: toolCalls,
      }).filter(([_, value]) => value != null),
    )
    const response = await axios.post(`${API_BASE_URL}/chats/${chatId}/`, requestBody)

    // We will get ChatResponse object in response.data
    return response.data
  },

  /**
   * Update a chat thread's title
   * @param {string} chatId - The ID of the chat to update
   * @param {string} title - The new title for the chat
   * @returns {Promise} Promise with updated chat data
   */
  updateChatTitle: async (chatId, title) => {
    const response = await axios.patch(`${API_BASE_URL}/chats/${chatId}/`, {
      new_title: title,
    })
    return response.data
  },

  /**
   * Delete a chat thread
   * @param {string} chatId - The ID of the chat to delete
   * @returns {Promise} Promise with deleted chat data
   */
  deleteChat: async (chatId) => {
    const response = await axios.delete(`${API_BASE_URL}/chats/${chatId}/`)
    return response.data
  },

  /**
   * Get table data for a chat thread
   * @param {string} chatId - The ID of the chat
   * @param {number} stepInChatHistory - The step in chat history to get table data for
   * @returns {Promise} Promise with table data
   */
  getTableData: async (chatId, stepInChatHistory, page) => {
    // TODO: For this specific API, the backend expects a 1-indexed step. Remove after fix.
    const actualStep = stepInChatHistory + 1
    const actualPage = page + 1

    const response = await axios.get(`${API_BASE_URL}/chat/table/${chatId}/`, {
      params: {
        step_in_chat_history: actualStep,
        page: actualPage,
      },
    })
    return response.data
  },
}

export default chatService
